/**
 * ListingPaidPeriodIndicator
 *
 * A listing's paid period (BR-92/BR-94) as plain text, a warning chip or a call to action,
 * in the two places it appears: the "Live period" column of the listings index and the
 * listing detail header. Dates render in the unit's timezone.
 *
 *   live            "Until Nov 4"            (index)   /  "Live until Nov 4"       (header)
 *   renewable       "Ends in 2 days" + Renew (index)   /  "Ends Oct 7 · in 2 days" + Renew N days (header)
 *   paid, not live  [Paid] badge + "through Nov 4" (index and header) — draft, paused or closed
 *                   listing whose unit still has a current period: the PAYMENT state, shown beside (never instead of)
 *                   the publication status badge (BR-110)
 *   expired         [Expired] badge + "Ended Oct 7" + Republish (index) /  [Expired] badge + "Ended Oct 7 · no longer live on Resida" (header)
 *
 * The listing STATUS badge is publication-only (Draft/Active/Paused/Closed); this component alone carries the
 * payment state (Paid / Expired / ending). An expired listing is a paused one whose unit has no current period,
 * or a draft that was paid once (lastPeriodEndedAt) and has none now.
 *
 * Renew shows ONLY when `paidPeriod.renewable` is true (the server's final-window-and-nothing-
 * queued flag) on an active listing.
 */

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { useUnitTimezone } from '../../hooks/useUnitTimezone';
import { formatPeriodDate } from '../../api/billing';
import type { Listing } from '../../lib/leasing.api';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** A paused listing, or a once-paid draft, whose unit has no current paid period lapsed (its period ended). */
export function isListingExpired(
  listing: Pick<Listing, 'status' | 'paidPeriod' | 'lastPeriodEndedAt'>,
): boolean {
  if (listing.paidPeriod !== null) return false;
  return (
    listing.status === 'paused' || (listing.status === 'draft' && listing.lastPeriodEndedAt != null)
  );
}

/** Whole days left (rounded up) until `iso`, never below 0. */
function daysUntil(iso: string): number {
  // eslint-disable-next-line no-restricted-syntax -- `iso` is a real instant (paid period end), so elapsed time is correct; not a date-only field
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / MS_PER_DAY));
}

/** "in 2 days" / "in 1 day" / "today". */
function relativeDays(days: number): string {
  if (days === 0) return 'today';
  return `in ${days} day${days === 1 ? '' : 's'}`;
}

interface ListingPaidPeriodIndicatorProps {
  listing: Listing;
  variant: 'index' | 'header';
  /**
   * Opens the renew dialog (only offered when `paidPeriod.renewable`). Omit it where renewing is not
   * offered (the guided setup only ever shows a draft): the indicator then shows no Renew button.
   */
  onRenew?: () => void;
  /** Opens the publish dialog (index variant, expired rows). */
  onRepublish?: () => void;
  /** The plan's listing period, for the header's "Renew N days" label. */
  periodDays?: number | null;
}

export function ListingPaidPeriodIndicator({
  listing,
  variant,
  onRenew,
  onRepublish,
  periodDays,
}: ListingPaidPeriodIndicatorProps) {
  const { data: timezone, isPending, isError } = useUnitTimezone(listing.unitId);
  const { paidPeriod } = listing;
  const expired = isListingExpired(listing);
  // A failed timezone lookup degrades to an explicit UTC rendering, never a silent browser-local one.
  const tz = isError || !timezone ? 'UTC' : timezone;
  const endedOn =
    expired && !isPending && listing.lastPeriodEndedAt
      ? formatPeriodDate(listing.lastPeriodEndedAt, tz)
      : null;
  const testId = (suffix: string) => `listing-period-${suffix}-${listing.id}`;

  if (paidPeriod === null && !expired) {
    return variant === 'index' ? (
      <span className="text-muted-foreground" data-testid={testId('none')}>—</span>
    ) : null;
  }

  if (expired) {
    const endedText = endedOn ? `Ended ${endedOn}` : 'Ended';
    return (
      <span className="inline-flex flex-wrap items-center gap-2" data-testid={testId('expired')}>
        <Badge variant="warning">Expired</Badge>
        <span className="text-sm text-muted-foreground">
          {variant === 'header' ? `${endedText} · no longer live on Resida` : endedText}
        </span>
        {variant === 'index' && onRepublish && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-[44px]"
            data-testid={testId('republish')}
            onClick={(e) => {
              e.stopPropagation();
              onRepublish();
            }}
          >
            Republish
          </Button>
        )}
      </span>
    );
  }

  if (paidPeriod === null) return null;

  if (isPending) return <Skeleton className="h-5 w-24" data-testid={testId('loading')} />;
  const endsOn = formatPeriodDate(paidPeriod.endsAt, tz);
  const days = daysUntil(paidPeriod.endsAt);

  // Paid but not live (draft, paused, closed): the payment badge, never "Live until".
  if (listing.status !== 'active') {
    return (
      <span className="inline-flex flex-wrap items-center gap-2" data-testid={testId('paid')}>
        <Badge variant="success">Paid</Badge>
        <span className="text-sm text-muted-foreground">through {endsOn}</span>
      </span>
    );
  }

  if (!paidPeriod.renewable) {
    return (
      <span className="text-sm text-foreground" data-testid={testId('live')}>
        {variant === 'header' ? `Live until ${endsOn}` : `Until ${endsOn}`}
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2" data-testid={testId('ending')}>
      <Badge variant="warning">
        {variant === 'header' ? `Ends ${endsOn} · ${relativeDays(days)}` : `Ends ${relativeDays(days)}`}
      </Badge>
      {onRenew && (
        <Button
          type="button"
          size="sm"
          variant={variant === 'header' ? 'default' : 'outline'}
          className="min-h-[44px]"
          data-testid={testId('renew')}
          onClick={(e) => {
            e.stopPropagation();
            onRenew();
          }}
        >
          {variant === 'header' && periodDays ? `Renew ${periodDays} days` : 'Renew'}
        </Button>
      )}
    </span>
  );
}
